/**
 * Global redirect helpers — single source of truth for "return to where you were" after auth.
 *
 * Standard query param: ?redirectTo=...
 * All login/signup links MUST use buildLoginUrl() / buildSignupUrl().
 *
 * Security: only relative paths starting with "/" (but not "//") are allowed.
 */

const PARAM = 'redirectTo';

/** Get current location as a return-to value (pathname + search + hash). */
export function getCurrentReturnTo(): string {
  return window.location.pathname + window.location.search + window.location.hash;
}

/** Encode a path for use in a query string value. */
export function encodeReturnTo(path: string): string {
  return encodeURIComponent(path);
}

/**
 * Validate a redirect path is safe (no open redirect).
 * Returns true if the path starts with "/" and doesn't escape to an external URL.
 */
export function isSafeRedirectTo(path: string | null | undefined): boolean {
  if (!path || typeof path !== 'string') return false;
  const trimmed = path.trim();
  if (!trimmed.startsWith('/')) return false;
  if (trimmed.startsWith('//')) return false;
  if (/https?:/i.test(trimmed)) return false;
  if (trimmed.includes(':')) {
    // Block javascript:, data:, etc. but allow colons in query/hash
    const beforeQuery = trimmed.split('?')[0].split('#')[0];
    if (beforeQuery.includes(':')) return false;
  }
  return true;
}

/**
 * Decode and validate a redirectTo value from query params.
 * Returns the safe path, or null if invalid/missing.
 */
export function decodeReturnTo(value: string | null | undefined): string | null {
  if (!value) return null;
  const decoded = decodeURIComponent(value);
  return isSafeRedirectTo(decoded) ? decoded : null;
}

/**
 * Read the redirectTo param from a URL search string.
 * Also checks legacy param names (next, redirect, returnTo) for backward compat.
 */
export function getRedirectToFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  const candidates = [PARAM, 'next', 'redirect', 'returnTo'];
  for (const key of candidates) {
    const val = params.get(key);
    const safe = decodeReturnTo(val);
    if (safe) return safe;
  }
  return null;
}

/** Build a /login URL with redirectTo pointing to the current page. */
export function buildLoginUrl(returnTo?: string): string {
  const path = returnTo ?? getCurrentReturnTo();
  if (!isSafeRedirectTo(path) || path === '/login' || path === '/') {
    return '/login';
  }
  return `/login?${PARAM}=${encodeReturnTo(path)}`;
}

/** Build a /login URL with the signup tab pre-selected. */
export function buildSignupUrl(returnTo?: string): string {
  const path = returnTo ?? getCurrentReturnTo();
  if (!isSafeRedirectTo(path) || path === '/login' || path === '/') {
    return '/login?tab=signup';
  }
  return `/login?tab=signup&${PARAM}=${encodeReturnTo(path)}`;
}
